import {
  Button,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  SimpleGrid,
  useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { ToolbarButton, type ToolbarButtonProps } from "./ToolbarButton";

export interface Swatch {
  name: string;
  value: string;
}

interface Props {
  label: string;
  swatches: Swatch[];
  current: string | undefined;
  removeLabel: string;
  onSelect: (value: string) => void;
  onRemove: () => void;
  trigger: ToolbarButtonProps;
  children: ReactNode;
}

const rgbFunction = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/;

export const normaliseColour = (colour: string | undefined) => {
  const value = colour?.trim().toLowerCase();
  const channels = value?.match(rgbFunction)?.slice(1, 4);
  if (!channels) return value;
  return "#" + channels.map((channel) => Number(channel).toString(16).padStart(2, "0")).join("");
};

export const ColorPalette = ({ label, swatches, current, removeLabel, onSelect, onRemove, trigger, children }: Props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const firstFocusRef = useRef<HTMLButtonElement>(null);
  const [openedByKeyboard, setOpenedByKeyboard] = useState(false);
  useEffect(() => {
    if (!isOpen || !openedByKeyboard) return;
    const frame = requestAnimationFrame(() => firstFocusRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [isOpen, openedByKeyboard]);
  const noteHowItOpens = (event: KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") setOpenedByKeyboard(true);
  };
  const choose = (apply: () => void) => {
    apply();
    onClose();
  };
  const returnFocusOnEscape = (event: KeyboardEvent) => {
    if (event.key !== "Escape") return;
    event.stopPropagation();
    event.preventDefault();
    onClose();
    triggerRef.current?.focus();
  };
  const inUse = normaliseColour(current);
  const focusedFirst = swatches.find((swatch) => normaliseColour(swatch.value) === inUse) ?? swatches[0];

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-start" isLazy returnFocusOnClose={false}>
      <PopoverTrigger>
        <ToolbarButton
          ref={triggerRef}
          label={label}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          onKeyDown={noteHowItOpens}
          onPointerDown={() => setOpenedByKeyboard(false)}
          {...trigger}
        >
          {children}
        </ToolbarButton>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          width="auto"
          aria-label={label}
          rootProps={{ zIndex: "popover" }}
          onKeyDown={returnFocusOnEscape}
        >
          <PopoverBody>
            <SimpleGrid columns={swatches.length > 5 ? 4 : 5} spacing="2" role="group" aria-label={label}>
              {swatches.map((swatch) => {
                const isInUse = inUse === normaliseColour(swatch.value);
                return (
                  <Button
                    key={swatch.value}
                    ref={swatch === focusedFirst ? firstFocusRef : undefined}
                    size="sm"
                    minWidth="7"
                    height="7"
                    p="0"
                    rounded="full"
                    borderWidth={isInUse ? "2px" : "1px"}
                    borderColor={isInUse ? "smPrimary" : "smBorder"}
                    backgroundColor={swatch.value}
                    _hover={{ backgroundColor: swatch.value }}
                    aria-label={swatch.name}
                    aria-pressed={isInUse}
                    onClick={() => choose(() => onSelect(swatch.value))}
                  />
                );
              })}
            </SimpleGrid>
            <Button size="xs" variant="ghost" mt="3" width="full" onClick={() => choose(onRemove)}>
              {removeLabel}
            </Button>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
